import React, { memo, useContext, useEffect, useReducer } from 'react';
import { useSnackbar } from 'notistack';
import { useParams, Redirect } from 'react-router-dom';
import { sendPlayerAnalytics } from '../../../catalog/Analytics';
import { WebsiteDesignType } from '../../../catalog/enums/WebsiteDesignType';
import { isValidObject, isValidString } from '../../../catalog/Validity';
import { context } from '../../../globalState/GlobalState';
import { SportsType } from '../../../catalog/enums/SportsType';
import { fetchIceHockeyGamePlayerAPI } from '../../apiCalls/games/iceHockey/IceHockeyGamePlayerAPICalls';
import { fetchRugbyGamePlayerAPI } from '../../apiCalls/games/rugby/RugbyGamePlayerAPICalls';
import { fetchPlayerAPI, fetchPlayerNameAPI } from '../../apiCalls/PlayerAPICalls';
import { fetchSeasonsofPlayersAPI } from '../../apiCalls/SeasonAPICalls';
import Player1 from '../../designs/player1/Player1';
import { createCustomTitleTag, createCustomDescriptionTag, createCustomKeywordsTag } from '../uiElements/helmet/MetaTags';
import TitleAndDescriptionTags from '../uiElements/helmet/TitleAndDescriptionTags';
import PlayerTags from '../uiElements/helmet/PlayerTags';
import { fetchLacrosseGamePlayerAPI } from '../../apiCalls/games/lacrosse/LacrosseGamePlayerAPICalls';

const initialState = {
    name: '',
    featureImage: '',
    profileImage: '',
    details: [],
    summary: '',
    bio: '',
    texts: [],
    seasons: [],
    alumni: false,
    deleted: true,
    stats: [],
    gameLogs: [],
    loadingSpinner: true,
    error: false,
};

function reducer(state, action) {
    switch (action.type) {
        case 'fetchPlayerName':
            return { ...state, name: action.name };
        case 'fetchPlayer':
            return { ...state,
                name: action.name,
                featureImage: action.featureImage,
                profileImage: action.profileImage,
                details: action.details,
                summary: action.summary,
                bio: action.bio,
                texts: action.texts,
                alumni: action.alumni,
                deleted: action.deleted,
                overridingStats: action.overridingStats };
        case 'fetchStatsAndGameLogs':
            return { ...state, stats: action.stats, gameLogs: action.gameLogs };
        case 'fetchSeasons':
            return { ...state, seasons: action.seasons };
        case 'setLoadingSpinner':
            return { ...state, loadingSpinner: action.loadingSpinner };
        case 'setError':
            return { ...state, error: true };
        default:
            return initialState;
    }
}

const Player = () => {
    const { globalState } = useContext(context);
    const params = useParams();
    const [state, setState] = useReducer(reducer, initialState);
    const { enqueueSnackbar } = useSnackbar();

    useEffect(() => {
        sendPlayerAnalytics(globalState, state.name, params.playerid);
    }, [globalState, state.name]);

    useEffect(() => {
        async function fetchPlayer() {
            if (isValidString(globalState.accountId) && isValidString(params.playerid)) {
                setState({ type: 'setLoadingSpinner', loadingSpinner: true });
                await fetchPlayerNameAPI(globalState.accountId, params.playerid, setState, enqueueSnackbar);
                await fetchPlayerAPI(globalState.accountId, params.playerid, setState, enqueueSnackbar);
                setState({ type: 'setLoadingSpinner', loadingSpinner: false });
                if (globalState.sportsType === SportsType.WOMENSICEHOCKEY || globalState.sportsType === SportsType.MENSICEHOCKEY) {
                    await fetchIceHockeyGamePlayerAPI(globalState.sportsType, globalState.accountId, params.playerid, setState, enqueueSnackbar);
                } else if (globalState.sportsType === SportsType.WOMENSRUGBY || globalState.sportsType === SportsType.MENSRUGBY) {
                    await fetchRugbyGamePlayerAPI(globalState.sportsType, globalState.accountId, params.playerid, setState, enqueueSnackbar);
                } else if (globalState.sportsType === SportsType.WOMENSLACROSSE || globalState.sportsType === SportsType.MENSLACROSSE) {
                    await fetchLacrosseGamePlayerAPI(globalState.sportsType, globalState.accountId, params.playerid, setState, enqueueSnackbar);
                }
                await fetchSeasonsofPlayersAPI(globalState.accountId, params.playerid, setState, enqueueSnackbar);
            }
        }

        fetchPlayer();
    }, [globalState.accountId, params.playerid]);

    const getPlayerDesign = () => {
        if (!isValidObject(globalState) || !isValidObject(globalState.websiteStyles)) {
            return null;
        }

        if (globalState.websiteStyles.design.player === WebsiteDesignType.DESIGN_1) {
            return (
                <Player1
                    name={state.name}
                    alumni={state.alumni}
                    featureImage={state.featureImage}
                    profileImage={state.profileImage}
                    details={state.details}
                    summary={state.summary}
                    bio={state.bio}
                    texts={state.texts}
                    seasons={state.seasons}
                    stats={state.stats}
                    gameLogs={state.gameLogs}
                    deleted={state.deleted}
                    loadingSpinner={state.loadingSpinner}
                />
            );
        }
        return null;
    };

    if (state.error) {
        return <Redirect to="/notfound" />;
    }

    return (
        <>
            {isValidString(state.name) ? (
                <>
                    <TitleAndDescriptionTags
                        title={createCustomTitleTag(state.name, globalState)}
                        description={createCustomDescriptionTag(`Check out the profile of ${state.name}`, globalState)}
                        keywords={createCustomKeywordsTag(`${state.name}, Profile, Player Profile`, globalState)}
                    />
                    <PlayerTags title={state.name} />
                </>
            ) : null}
            {getPlayerDesign()}
        </>
    );
};

export default memo(Player);
